<template>
  <div class="perf-overview">
    <div class="row-2">
      <section
        v-for="col in columns"
        :key="col.key"
        class="card"
        :class="'card--' + col.theme"
      >
        <h3 class="section-title"><span class="title-bar" :class="'title-bar--' + col.theme" />{{ col.title }}</h3>

        <!-- 顶部：本年/本月新增销售台数 / 销售额 -->
        <div class="perf-summary-panel">
        <div class="metric-pair">
          <div class="metric-block">
            <div class="metric-label">{{ col.periodPrefix }}新增销售台数</div>
            <div class="metric-value">
              <span class="num" :class="'num--' + col.theme">{{ fmtNum(col.data.salesUnits.value) }}</span>
              <span class="unit">台</span>
            </div>
            <div class="trend-row">
              <span class="trend" :class="col.data.salesUnits.momUp ? 'trend--up' : 'trend--down'">
                <span class="field-label">环比</span>
                <span class="field-value">{{ fmtRate(col.data.salesUnits.mom) }}%</span>
                <dashboard-icons :name="col.data.salesUnits.momUp ? 'trendUp' : 'trendDown'" size="12" :color="trendArrowColor(col.data.salesUnits.momUp)" />
              </span>
              <span class="trend" :class="col.data.salesUnits.yoyUp ? 'trend--up' : 'trend--down'">
                <span class="field-label">同比</span>
                <span class="field-value">{{ fmtRate(col.data.salesUnits.yoy) }}%</span>
                <dashboard-icons :name="col.data.salesUnits.yoyUp ? 'trendUp' : 'trendDown'" size="12" :color="trendArrowColor(col.data.salesUnits.yoyUp)" />
              </span>
            </div>
          </div>
          <div class="metric-block metric-block--amount">
            <div class="metric-label">{{ col.periodPrefix }}新增销售额</div>
            <div class="metric-value">
              <span class="num" :class="'num--' + col.theme">{{ fmtAmount(col.data.salesRevenue.value) }}</span>
              <span class="unit">元</span>
            </div>
            <div class="trend-row">
              <span class="trend" :class="col.data.salesRevenue.momUp ? 'trend--up' : 'trend--down'">
                <span class="field-label">环比</span>
                <span class="field-value">{{ fmtRate(col.data.salesRevenue.mom) }}%</span>
                <dashboard-icons :name="col.data.salesRevenue.momUp ? 'trendUp' : 'trendDown'" size="12" :color="trendArrowColor(col.data.salesRevenue.momUp)" />
              </span>
              <span class="trend" :class="col.data.salesRevenue.yoyUp ? 'trend--up' : 'trend--down'">
                <span class="field-label">同比</span>
                <span class="field-value">{{ fmtRate(col.data.salesRevenue.yoy) }}%</span>
                <dashboard-icons :name="col.data.salesRevenue.yoyUp ? 'trendUp' : 'trendDown'" size="12" :color="trendArrowColor(col.data.salesRevenue.yoyUp)" />
              </span>
            </div>
          </div>
        </div>
        </div>

        <!-- 类型卡片：时间型 / 流量型 / 买断型 -->
        <div class="perf-type-cards">
          <div
            v-for="(cat, idx) in col.data.categories"
            :key="cat.key"
            class="perf-type-card"
            :class="['perf-type-card--' + cat.key, 'perf-type-card--' + col.theme, 'perf-type-card--idx' + idx]"
          >
            <span class="perf-type-watermark" aria-hidden="true">
              <dashboard-icons v-if="cat.key !== 'buyout'" :name="cat.key === 'time' ? 'time' : 'traffic'" :size="56" color="rgba(255,255,255,0.22)" />
              <span v-else class="perf-buyout-mark">买</span>
            </span>
            <div class="perf-type-head">
              <span class="perf-type-icon-badge" :class="'perf-type-icon-badge--' + col.theme">
                <dashboard-icons v-if="cat.key !== 'buyout'" :name="cat.key === 'time' ? 'time' : 'traffic'" size="16" :color="iconAccent(col.theme, cat.key)" />
                <span v-else class="perf-buyout-icon">买</span>
              </span>
              <span class="perf-type-title">{{ cat.label }}</span>
            </div>
            <div class="perf-type-foot">
              <div class="perf-type-count">
                <span class="perf-type-count-num">{{ fmtNum(cat.units) }}</span>
                <span class="perf-type-count-unit">台</span>
              </div>
              <div class="perf-type-amt">
                <span class="perf-type-amt-num">{{ fmtAmount(cat.amount) }}</span>
                <span class="perf-type-amt-unit">元</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 按渠道分布 -->
        <div class="perf-dist-card">
          <div class="perf-dist-head">
            <dashboard-icons name="channel" size="16" :color="headIconColor(col.theme)" />
            <el-tooltip placement="top" effect="dark">
              <template #content>渠道为分析拆分，同一笔可计入多个渠道，合计可与顶部销售额不一致</template>
              <span class="perf-dist-head-text">
                {{ col.data.channelTitle }}
                <app-icon icon="el-icon-question" class="perf-tip-icon" />
              </span>
            </el-tooltip>
          </div>
          <div v-for="ch in col.data.byChannel" :key="ch.label" class="perf-channel-row">
            <div class="perf-channel-left">
              <span class="perf-bullet" :class="'perf-bullet--' + col.theme" />
              <span class="perf-channel-name">{{ ch.label }}</span>
            </div>
            <div class="perf-channel-amt"><span class="field-value">{{ fmtAmount(ch.amount) }}</span><span class="field-unit">元</span></div>
          </div>
        </div>

        <!-- 按业务分布 -->
        <div class="perf-dist-card">
          <div class="perf-dist-head">
            <dashboard-icons name="business" size="16" :color="headIconColor(col.theme)" />
            <span>{{ col.data.businessTitle }}</span>
          </div>
          <div
            v-for="biz in col.data.byBusiness"
            :key="biz.label"
            class="perf-biz-row"
            :class="{ 'perf-biz-row--simple': !biz.subs }"
          >
            <div class="perf-biz-left">
              <span class="perf-bullet" :class="'perf-bullet--' + col.theme" />
              <div class="perf-biz-main">
                <div class="perf-biz-name">{{ biz.label }}</div>
                <div class="perf-biz-amt"><span class="field-value">{{ fmtAmount(biz.amount) }}</span><span class="field-unit">元</span></div>
              </div>
            </div>
            <div v-if="!biz.subs" class="perf-biz-units">
              <span class="field-value">{{ fmtNum(biz.units) }}</span><span class="field-unit">台</span>
            </div>
            <div v-else class="perf-biz-subs">
              <div v-for="sub in biz.subs" :key="sub.label" class="perf-biz-sub">
                <span class="perf-biz-sub-label">{{ sub.label }}</span>
                <span class="perf-biz-sub-val"><span class="field-value">{{ fmtAmount(sub.amount) }}</span><span class="field-unit">元</span></span>
                <span class="perf-biz-sub-val"><span class="field-value">{{ fmtNum(sub.units) }}</span><span class="field-unit">台</span></span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import DashboardIcons from './DashboardIcons.vue'
import { getDashboardPerformanceOverview } from '@/api/dashboard'
import { formatDashboardNum, formatDashboardAmount } from './dashboardPerformanceMock'

function emptyTrend() {
  return { value: 0, mom: 0, momUp: true, yoy: 0, yoyUp: true }
}

function emptyPeriod(suffix) {
  return {
    salesUnits: emptyTrend(),
    salesRevenue: emptyTrend(),
    categories: [
      { key: 'time', label: '时间型', units: 0, amount: 0 },
      { key: 'traffic', label: '流量型', units: 0, amount: 0 },
      { key: 'buyout', label: '买断型', units: 0, amount: 0 },
    ],
    channelTitle: `按渠道分布${suffix}`,
    businessTitle: `按业务分布${suffix}`,
    byChannel: [
      { label: '直营销售收入', amount: 0 },
      { label: '经销商销售收入', amount: 0 },
      { label: '电商销售收入', amount: 0 },
      { label: '运维销售收入', amount: 0 },
    ],
    byBusiness: [
      { label: '买断收入', amount: 0, units: 0, subs: null },
      { label: '租赁收入', amount: 0, subs: [
        { label: '新增', amount: 0, units: 0 },
        { label: '续费', amount: 0, units: 0 },
      ] },
      { label: '流量收入', amount: 0, subs: [
        { label: '新增', amount: 0, units: 0 },
        { label: '续费', amount: 0, units: 0 },
      ] },
      { label: '服务包收入', amount: 0, subs: [
        { label: '新增', amount: 0, units: 0 },
        { label: '续费', amount: 0, units: 0 },
      ] },
    ],
  }
}

function emptyPerformance() {
  return {
    annual: emptyPeriod('（本年）'),
    monthly: emptyPeriod('（本月）'),
  }
}

export default {
  name: 'DashboardPerformanceOverview',
  components: { DashboardIcons },
  data() {
    return {
      d: emptyPerformance(),
    }
  },
  computed: {
    columns() {
      return [
        { key: 'annual', title: '本年度业务概况', theme: 'annual', periodPrefix: '本年', data: this.d.annual || emptyPeriod('（本年）') },
        { key: 'monthly', title: '本月业务概况', theme: 'monthly', periodPrefix: '本月', data: this.d.monthly || emptyPeriod('（本月）') },
      ]
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      try {
        const res = await getDashboardPerformanceOverview()
        if (res && res.data) {
          this.d = {
            annual: { ...emptyPeriod('（本年）'), ...(res.data.annual || {}) },
            monthly: { ...emptyPeriod('（本月）'), ...(res.data.monthly || {}) },
          }
        }
      } catch (e) {
        ElMessage.error(e.message || '加载业绩概况失败')
      }
    },
    fmtNum: formatDashboardNum,
    fmtAmount: formatDashboardAmount,
    fmtRate(n) {
      const v = Number(n)
      if (Number.isNaN(v)) return '0.00'
      return v.toFixed(2)
    },
    trendArrowColor(isUp) {
      return isUp ? '#f5222d' : '#52c41a'
    },
    headIconColor(theme) {
      return theme === 'annual' ? '#1890ff' : '#9254de'
    },
    iconAccent(theme, key) {
      if (theme === 'monthly') {
        return key === 'time' ? '#9b6fd6' : '#7c4dbd'
      }
      return key === 'time' ? '#5b9fff' : '#3d7ee6'
    },
  },
}
</script>

<style scoped>
/* 文字层级：参照设计稿（大→小：顶部 KPI 蓝字 > 渠道金额 > 类型卡金额 > 标题 > 环比） */
.field-label {
  color: #8c8c8c;
  font-size: 13px;
  font-weight: 400;
}

.field-value {
  color: #262626;
  font-size: 14px;
  font-weight: 600;
}

.field-unit {
  color: #262626;
  font-size: 14px;
  font-weight: 400;
}

.perf-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.row-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.row-2 > .card {
  min-width: 0;
}

.card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.section-title {
  display: flex;
  align-items: center;
  margin: 0 0 12px;
  font-size: 17px;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
}

/* 顶部 KPI 区（浅蓝底，与设计稿一致） */
.perf-summary-panel {
  background: #f0f6ff;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
}

.card--monthly .perf-summary-panel {
  background: #f6f0ff;
}

.title-bar {
  display: inline-block;
  width: 4px;
  height: 16px;
  margin-right: 8px;
  background: #1890ff;
  border-radius: 1px;
  flex-shrink: 0;
}

.title-bar--monthly {
  background: #9254de;
}

.metric-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 0;
}

.metric-block {
  min-width: 0;
}

.metric-label {
  font-size: 15px;
  color: #262626;
  font-weight: 600;
  margin-bottom: 8px;
  line-height: 1.3;
}

.metric-value {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  margin-bottom: 8px;
}

.metric-value .num {
  font-size: clamp(25px, 2.6vw, 29px);
  font-weight: 600;
  line-height: 1.1;
}

.num--annual {
  color: #1890ff;
}

.num--monthly {
  color: #9254de;
}

.metric-value .unit {
  font-size: 15px;
  font-weight: 400;
  line-height: 1;
  padding-bottom: 3px;
}

.card--annual .metric-value .unit {
  color: #69b1ff;
}

.card--monthly .metric-value .unit {
  color: #b37feb;
}

.trend-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.trend {
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.trend .field-label {
  font-size: 13px;
  color: #8c8c8c;
  font-weight: 400;
}

.trend .field-value {
  font-size: 13px;
  color: #262626;
  font-weight: 600;
}

/* 三列类型卡 */
.perf-type-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.perf-type-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 108px;
  padding: 12px 14px;
  border-radius: 8px;
  color: #fff;
  overflow: hidden;
  box-sizing: border-box;
}

.card--annual .perf-type-card--time {
  background: #5b9fff;
}

.card--annual .perf-type-card--traffic {
  background: #4a8ef5;
}

.card--annual .perf-type-card--buyout {
  background: #2f6fd6;
}

.card--monthly .perf-type-card--time {
  background: #b37feb;
}

.card--monthly .perf-type-card--traffic {
  background: #9b6fd6;
}

.card--monthly .perf-type-card--buyout {
  background: #7c4dbd;
}

.perf-type-watermark {
  position: absolute;
  top: 4px;
  right: 6px;
  z-index: 0;
  line-height: 0;
  pointer-events: none;
}

.perf-buyout-mark {
  font-size: 49px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.2);
  line-height: 1;
}

.perf-type-head {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
}

.perf-type-icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #fff;
  border-radius: 6px;
  flex-shrink: 0;
}

.perf-buyout-icon {
  font-size: 15px;
  font-weight: 700;
  color: #2f6fd6;
  line-height: 1;
}

.card--monthly .perf-buyout-icon {
  color: #7c4dbd;
}

.perf-type-title {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
}

.perf-type-foot {
  position: relative;
  z-index: 1;
  margin-top: 12px;
}

.perf-type-count {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  line-height: 1.15;
}

.perf-type-count-num {
  font-size: clamp(19px, 2vw, 23px);
  font-weight: 600;
}

.perf-type-count-unit {
  font-size: 14px;
  font-weight: 400;
  padding-bottom: 2px;
  opacity: 0.95;
}

.perf-type-amt {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  margin-top: 8px;
  line-height: 1.15;
}

.perf-type-amt-num {
  font-size: clamp(19px, 2vw, 23px);
  font-weight: 600;
  opacity: 0.98;
}

.perf-type-amt-unit {
  font-size: 14px;
  font-weight: 400;
  padding-bottom: 2px;
  opacity: 0.95;
}

/* 分布白卡片 */
.perf-dist-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 12px;
}

.perf-dist-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  line-height: 1.3;
}

.perf-dist-head span {
  font-size: 15px;
}

.perf-dist-head-text {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: help;
}

.perf-tip-icon {
  color: #8c8c8c;
  font-size: 14px;
}

.perf-bullet {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 6px;
}

.perf-bullet--annual {
  background: #1890ff;
}

.perf-bullet--monthly {
  background: #9254de;
}

.perf-channel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid #f5f5f5;
  min-width: 0;
}

.perf-channel-row:first-of-type {
  border-top: none;
  padding-top: 0;
}

.perf-channel-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.perf-channel-row .perf-bullet {
  margin-top: 0;
}

.perf-channel-name {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
  min-width: 0;
}

.perf-channel-amt {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  flex-shrink: 0;
  margin-left: auto;
  text-align: right;
  white-space: nowrap;
  line-height: 1.4;
}

.perf-channel-amt .field-value {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
}

.perf-channel-amt .field-unit {
  font-size: 15px;
  font-weight: 400;
  color: #262626;
}

/* 按业务分布 */
.perf-biz-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 8px 16px;
  align-items: start;
  padding: 10px 0;
  border-top: 1px solid #f5f5f5;
}

.perf-biz-row:first-of-type {
  border-top: none;
  padding-top: 0;
}

.perf-biz-left {
  display: flex;
  gap: 8px;
  min-width: 0;
}

.perf-biz-name {
  font-size: 14px;
  font-weight: 400;
  color: #262626;
  margin-bottom: 6px;
  line-height: 1.3;
}

.perf-biz-amt {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  line-height: 1.2;
}

.perf-biz-amt .field-value {
  font-size: clamp(18px, 2vw, 23px);
  font-weight: 600;
  line-height: 1.15;
}

.perf-biz-amt .field-unit {
  font-size: 14px;
  font-weight: 400;
  padding-bottom: 2px;
  color: #262626;
}

.perf-biz-units {
  display: flex;
  align-items: flex-end;
  gap: 2px;
}

.perf-biz-units .field-value {
  font-size: clamp(18px, 2vw, 23px);
  font-weight: 600;
}

.perf-biz-units .field-unit {
  font-size: 14px;
  font-weight: 400;
  padding-bottom: 2px;
}

.perf-biz-units {
  text-align: right;
  align-self: center;
  white-space: nowrap;
}

.perf-biz-subs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.perf-biz-sub {
  display: grid;
  grid-template-columns: 44px 1fr 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 13px;
}

.perf-biz-sub-label {
  color: #8c8c8c;
  font-size: 13px;
  font-weight: 400;
}

.perf-biz-sub-val {
  text-align: right;
  white-space: nowrap;
}

.perf-biz-sub-val {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
}

.perf-biz-sub-val .field-value {
  font-size: 13px;
  font-weight: 600;
}

.perf-biz-sub-val .field-unit {
  font-size: 13px;
  font-weight: 400;
  padding-bottom: 1px;
}

@media (max-width: 1200px) {
  .row-2 {
    grid-template-columns: 1fr;
  }
  .perf-type-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 992px) {
  .metric-pair {
    grid-template-columns: 1fr;
  }
  .perf-biz-row {
    grid-template-columns: 1fr;
  }
  .perf-biz-units {
    text-align: left;
  }
}
</style>
